import React from 'react';
import { getRUM } from '../telemetry/rum';

interface State {
  hasError: boolean;
  error: Error | null;
}

interface Props {
  children: React.ReactNode;
  /** Custom fallback — receives error + retry callback. */
  fallback?: (error: Error, retry: () => void) => React.ReactNode;
}

/**
 * Error boundary for Suspense regions. Catches lazy-load failures and
 * renders a retry button instead of a blank page.
 * Reports to CloudWatch RUM (Sydney) when initialised; otherwise silent.
 */
export class AppErrorBoundary extends React.Component<Props, State> {
  state: State = { hasError: false, error: null };

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo): void {
    const rum = getRUM();
    rum?.recordError(error);
    void info;
  }

  private retry = (): void => {
    this.setState({ hasError: false, error: null });
  };

  render(): React.ReactNode {
    if (this.state.hasError && this.state.error) {
      if (this.props.fallback) {
        return this.props.fallback(this.state.error, this.retry);
      }
      return (
        <div
          role="alert"
          className="flex flex-col items-center justify-center py-16 px-4 text-center"
        >
          <div className="w-12 h-12 rounded-full bg-red-50 flex items-center justify-center mb-4">
            <span className="text-red-500 text-xl font-bold">!</span>
          </div>
          <h2 className="text-base font-semibold text-gray-900 mb-1">
            Something went wrong
          </h2>
          <p className="text-sm text-gray-500 mb-4 max-w-sm">
            {this.state.error.message || 'An unexpected error occurred.'}
          </p>
          <button
            onClick={this.retry}
            className="px-4 py-2 bg-gray-900 text-white text-sm font-medium rounded-lg hover:bg-gray-700 transition-colors"
          >
            Try again
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

export default AppErrorBoundary;
